import { useCallback, useEffect, useMemo, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TableKit } from "@tiptap/extension-table/kit";
import { Markdown } from "@tiptap/markdown";
import { Editor, Extension, Mark, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { cn } from "@/lib/utils";
import { summaryEvidenceExcludedRanges } from "./summaryEvidenceRanges";

export interface EvidenceHighlight {
  text: string;
  /** The ICD-10 code this evidence supports */
  code: string;
  /** Why this text was identified as evidence (from DiagnosisAction.reason) */
  reason?: string;
  /** Whether this highlight's code is currently focused by the user */
  isFocused: boolean;
  /** Whether this is the specific quote the user is iterating to (scroll target) */
  isActiveQuote: boolean;
  /** Source document type; codeable summaries exclude reviewer-only evidence locations. */
  fileIdentifier: string;
  /** Optional color variant — "unmatched" renders orange instead of default yellow/sky */
  highlightVariant?: "unmatched";
  /** Set for an indication verdict's mention: drawn as an outline over the evidence fills, not as one. */
  indication?: IndicationHighlightInfo;
}

export interface IndicationHighlightInfo {
  /** `<indication position>:<mention index>` */
  key: string;
  title: string;
  verdict: "coded" | "not-coded";
  /** The checks could not run: a failed check, not a rule's decision. */
  isNotClassified: boolean;
  definition: string | null;
  reason: string;
  sourceLabel: string;
  personalHistory: boolean;
  absent: boolean;
}

export const ACTIVE_INDICATION_MENTION_ID = "active-indication-mention";

const evidenceHighlightKey = new PluginKey("evidenceHighlight");

/**
 * Keeps `<a id="…">` jump targets (the codeable summary's section and finding anchors) in the rendered
 * document. Without a mark that claims them, ProseMirror drops the element and every in-document link
 * points at nothing.
 */
const AnchorTarget = Mark.create({
  name: "anchorTarget",
  inclusive: false,
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("id"),
        renderHTML: (attributes) => (attributes.id ? { id: attributes.id } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "a[id]:not([href])" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["a", mergeAttributes(HTMLAttributes), 0];
  },
});

/** `<sub>` is how the summary demotes provenance and sub-labels; StarterKit has no such mark. */
const SubText = Mark.create({
  name: "subText",
  parseHTML() {
    return [{ tag: "sub" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["sub", mergeAttributes(HTMLAttributes), 0];
  },
});

/** `nodeName` gives each highlight its own wrapper, so overlapping ones nest instead of merging their attributes. */
const decorationAttrsOf = (hl: EvidenceHighlight) => {
  if (hl.indication) {
    const info = hl.indication;
    const tags = [info.personalHistory ? "personal history" : null, info.absent ? "absent" : null].filter(Boolean);
    const className = [
      "indication-hl",
      `indication-hl--${info.verdict}`,
      info.isNotClassified ? "indication-hl--failed" : null,
      hl.isActiveQuote ? "indication-hl--active" : null,
    ]
      .filter(Boolean)
      .join(" ");

    return {
      nodeName: "span",
      class: className,
      "data-indication-key": info.key,
      "data-indication-title": info.title,
      "data-indication-verdict": info.verdict,
      "data-indication-failed": info.isNotClassified ? "true" : "false",
      "data-indication-definition": info.definition ?? "",
      "data-indication-reason": info.reason,
      "data-indication-source": info.sourceLabel,
      "data-indication-tags": tags.join(","),
      ...(hl.isActiveQuote ? { id: ACTIVE_INDICATION_MENTION_ID } : {}),
    };
  }

  const variantClass = hl.highlightVariant === "unmatched" ? " evidence-hl--unmatched" : "";
  const className = hl.isActiveQuote
    ? `evidence-hl evidence-hl--active${variantClass}`
    : hl.isFocused
      ? `evidence-hl evidence-hl--focused${variantClass}`
      : `evidence-hl${variantClass}`;

  return {
    nodeName: "span",
    class: className,
    "data-code": hl.code,
    ...(hl.reason ? { "data-reason": hl.reason } : {}),
    ...(hl.isActiveQuote ? { id: "active-evidence-quote" } : {}),
  };
};

/**
 * Where each highlight's text sits in the document, or `null` when it cannot be found.
 * Matching is the first hit in the first block that has one, trying the exact text, then the
 * whitespace-normalised text, then the text with bracketed acronym expansions stripped.
 */
export function resolveHighlightRanges(
  doc: ProseMirrorNode,
  highlights: readonly EvidenceHighlight[],
): Array<{ from: number; to: number } | null> {
  const hasSummaryHighlight = highlights.some((hl) => hl.fileIdentifier === "codeable_summary");
  const excludedRanges = hasSummaryHighlight ? summaryEvidenceExcludedRanges(doc) : [];
  // A not-coded indication sits in the removed block, so its mention may match there.
  const indicationExcludedRanges = hasSummaryHighlight
    ? summaryEvidenceExcludedRanges(doc, { includeRemoved: true })
    : [];

  // Build a position-map for each leaf block node (paragraph, heading, etc.).
  // Concatenates text across all inline children so matches work even when
  // Markdown parsing splits text across multiple ProseMirror nodes.
  const blocks: { rawText: string; rawPosMap: number[]; reviewerOnly: boolean; reviewerOnlyForIndications: boolean }[] =
    [];

  doc.descendants((node, pos) => {
    if (!node.isBlock) return;
    // Skip container blocks (doc, blockquote, list, etc.)
    let hasBlockChild = false;
    node.forEach((child) => {
      if (child.isBlock) hasBlockChild = true;
    });
    if (hasBlockChild) return;

    const rawChars: string[] = [];
    const rawPosMap: number[] = [];
    node.descendants((child, childPos) => {
      if (child.isText && child.text) {
        for (let i = 0; i < child.text.length; i++) {
          rawChars.push(child.text[i]);
          rawPosMap.push(pos + 1 + childPos + i);
        }
      }
    });
    if (rawChars.length === 0) return;
    blocks.push({
      rawText: rawChars.join(""),
      rawPosMap,
      reviewerOnly: excludedRanges.some(({ from, to }) => pos >= from && pos < to),
      reviewerOnlyForIndications: indicationExcludedRanges.some(({ from, to }) => pos >= from && pos < to),
    });
  });

  return highlights.map((hl) => {
    const candidates: string[] = [hl.text];
    const wsNormalized = hl.text.replace(/\s+/g, " ").trim();
    if (wsNormalized !== hl.text) candidates.push(wsNormalized);
    if (/\[.*?\]/.test(hl.text)) {
      const stripped = hl.text.replace(/\s*\[.*?\]/g, "").trim();
      if (stripped.length > 0) candidates.push(stripped);
    }

    for (const search of candidates) {
      for (const block of blocks) {
        const isReviewerOnly = hl.indication ? block.reviewerOnlyForIndications : block.reviewerOnly;
        if (hl.fileIdentifier === "codeable_summary" && isReviewerOnly) continue;
        const idx = block.rawText.indexOf(search);
        if (idx === -1) continue;

        return { from: block.rawPosMap[idx], to: block.rawPosMap[idx + search.length - 1] + 1 };
      }
    }

    return null;
  });
}

/**
 * ProseMirror plugin that renders inline decorations (<mark>-like spans)
 * for evidence quotes. Highlights are pushed in via transaction meta so
 * they can be updated from React props without recreating the editor.
 */
const EvidenceHighlightExtension = Extension.create({
  name: "evidenceHighlight",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: evidenceHighlightKey,
        state: {
          init() {
            return DecorationSet.empty;
          },
          apply(tr, oldDecorations) {
            const meta = tr.getMeta(evidenceHighlightKey);
            if (meta !== undefined) {
              const highlights: EvidenceHighlight[] = meta.highlights;
              if (highlights.length === 0) return DecorationSet.empty;

              const doc = tr.doc;
              const ranges = resolveHighlightRanges(doc, highlights);
              const decorations = highlights.flatMap((hl, index) => {
                const range = ranges[index];
                return range ? [Decoration.inline(range.from, range.to, decorationAttrsOf(hl))] : [];
              });

              return DecorationSet.create(doc, decorations);
            }

            if (tr.docChanged) {
              return oldDecorations.map(tr.mapping, tr.doc);
            }
            return oldDecorations;
          },
        },
        props: {
          decorations(state) {
            return evidenceHighlightKey.getState(state);
          },
        },
      }),
    ];
  },
});

interface DocumentViewerProps {
  content: string;
  className?: string;
  /**
   * When true (default), content is parsed as Markdown.
   * When false, content is treated as plain text — no Markdown interpretation.
   * Use `markdown={false}` for raw clinical text (e.g. oncology letters, MDT reports)
   * where characters like `|` and `1.` are literal, not Markdown syntax.
   */
  markdown?: boolean;
  /** Evidence quotes to highlight inline within the rendered document. */
  highlights?: EvidenceHighlight[];
  /** Monotonic counter — bump to force decoration rebuild even when highlight positions are unchanged. */
  highlightVersion?: number;
}

/**
 * Markdown collapses any run of blank lines into a single paragraph break.
 * This pre-processor keeps the first \n\n (paragraph break) intact and
 * inserts a `<br>` for every additional blank line so visible vertical
 * space is preserved after the markdown parse.
 */
function preserveMultipleNewlines(text: string): string {
  return text.replace(/\n{3,}/g, (match) => {
    const extraBlanks = match.length - 2;
    // Each spacer <br> is its own block (blank line after it). Run them together and the <br> reads as
    // inline HTML opening a paragraph, which then absorbs whatever block follows — a heading on the
    // next line renders as literal "## …" text instead of a heading.
    return `\n\n${"<br>\n\n".repeat(extraBlanks)}`;
  });
}

// Tiptap's own click handler runs before React's, so it has to be off for the jump-link handler below
// to get a chance to intercept. External links still navigate natively via the browser.
const linkOptions = { openOnClick: false };

/** Convert plain text to HTML: escape HTML entities, convert newlines to markup. */
function plainTextToHtml(text: string): string {
  const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Split into paragraphs on double newlines, then convert single newlines to <br>
  return escaped
    .split(/\n\n+/)
    .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

const processContent = (content: string, markdown: boolean) =>
  markdown ? preserveMultipleNewlines(content) : plainTextToHtml(content);

const extensionsFor = (markdown: boolean) =>
  markdown
    ? [
        StarterKit.configure({ link: linkOptions }),
        TableKit,
        Markdown.configure({ markedOptions: { breaks: true, gfm: true } }),
        AnchorTarget,
        SubText,
        EvidenceHighlightExtension,
      ]
    : [StarterKit.configure({ link: linkOptions }), EvidenceHighlightExtension];

/** The indexes of the highlights whose text the viewer cannot find in this document. */
export function unresolvedHighlightIndexes(
  content: string,
  markdown: boolean,
  highlights: readonly EvidenceHighlight[],
): Set<number> {
  const editor = new Editor({
    extensions: extensionsFor(markdown),
    content: processContent(content, markdown),
    contentType: markdown ? "markdown" : "html",
    editable: false,
  });

  try {
    const ranges = resolveHighlightRanges(editor.state.doc, highlights);
    return new Set(ranges.flatMap((range, index) => (range === null ? [index] : [])));
  } finally {
    editor.destroy();
  }
}

export function DocumentViewer({
  content,
  className,
  markdown = true,
  highlights,
  highlightVersion,
}: DocumentViewerProps) {
  const processedContent = useMemo(() => processContent(content, markdown), [content, markdown]);

  const extensions = useMemo(() => extensionsFor(markdown), [markdown]);

  const lastScrolledVersion = useRef<number | undefined>(undefined);

  const editor = useEditor(
    {
      extensions,
      content: processedContent,
      contentType: markdown ? "markdown" : "html",
      editable: false,
    },
    [processedContent, markdown],
  );

  // In-document jump links (`#sec-…`, `#top`) scroll this pane to the matching anchor rather than
  // navigating. The anchor may be a table cell deep in the document, so scroll the element itself.
  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const link = (event.target as HTMLElement).closest("a[href^='#']");
    if (!link) return;

    event.preventDefault();
    const anchorId = link.getAttribute("href")?.slice(1);
    if (!anchorId) return;

    const target = event.currentTarget.querySelector(`[id="${CSS.escape(anchorId)}"]`);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Push highlights into the ProseMirror plugin via transaction meta.
  // This runs whenever highlights change OR when the editor is recreated (content change).
  useEffect(() => {
    if (!editor) return;
    editor.view.dispatch(
      editor.state.tr.setMeta(evidenceHighlightKey, {
        highlights: highlights ?? [],
      }),
    );

    const hasActiveQuote = highlights?.some((hl) => hl.isActiveQuote) ?? false;
    if (!hasActiveQuote || highlightVersion === lastScrolledVersion.current) return;

    const scrollToActiveQuote = () => {
      const activeQuote = editor.view.dom.querySelector(`#active-evidence-quote, #${ACTIVE_INDICATION_MENTION_ID}`);
      if (!activeQuote) return;

      lastScrolledVersion.current = highlightVersion;
      activeQuote.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    // A rebuilt editor has no mounted view yet, and reading view.dom before then throws.
    if (editor.isInitialized) {
      scrollToActiveQuote();
      return;
    }

    editor.on("create", scrollToActiveQuote);
    return () => {
      editor.off("create", scrollToActiveQuote);
    };
  }, [editor, highlights, highlightVersion]);

  return (
    <EditorContent
      editor={editor}
      onClick={handleClick}
      className={cn(
        "prose prose-sm max-w-none dark:prose-invert",
        "[&_.tiptap]:outline-none",
        // Jump links are in-document navigation, not external links — make them read as clickable.
        "[&_a[href^='#']]:cursor-pointer [&_a[href^='#']]:no-underline [&_a[href^='#']:hover]:underline",
        "[&_sub]:text-[0.75em] [&_sub]:text-muted-foreground [&_sub]:align-baseline",
        // Long OCR lines / tokens (e.g. dash rulers) must wrap, not force horizontal overflow.
        // overflow-wrap is inherited, so setting it here reaches paragraphs and table cells.
        "break-words",
        // Messy OCR text can parse into lists nested dozens of levels deep; both the <ul> and
        // the <li> at each level add inline padding, which together blow the width out by
        // >1000px. Stop indenting past the third level so pathological nesting can't force
        // horizontal overflow (legitimate 1–2 level lists are unaffected).
        "[&_ul_ul_ul]:ps-0 [&_ol_ol_ol]:ps-0 [&_li_li_li]:ps-0",
        // Rich table rendering for GFM markdown tables (e.g. pre-op endo assessments).
        // table-fixed makes columns divide the available width instead of expanding to content,
        // so wide OCR tables fit the resizable column rather than blowing out its width.
        "[&_table]:my-3 [&_table]:w-full [&_table]:table-fixed [&_table]:border-collapse [&_table]:border [&_table]:border-border",
        "[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:align-top [&_th]:font-medium [&_th]:break-words",
        "[&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_td]:break-words",
        // Evidence highlight styles — base, focused, active + hover + transitions
        "[&_.evidence-hl]:bg-yellow-100 [&_.evidence-hl]:text-gray-900 [&_.evidence-hl]:rounded-sm [&_.evidence-hl]:transition-[background-color,box-shadow] [&_.evidence-hl]:duration-200 [&_.evidence-hl]:ease-in-out",
        "[&_.evidence-hl:hover]:bg-yellow-200",
        "[&_.evidence-hl.evidence-hl--focused]:bg-sky-100",
        "[&_.evidence-hl.evidence-hl--focused:hover]:bg-sky-200",
        "[&_.evidence-hl.evidence-hl--active]:bg-sky-200 [&_.evidence-hl.evidence-hl--active]:shadow-[inset_0_-2px_0_theme(colors.sky.500)]",
        // Unmatched term highlight styles — orange variant
        "[&_.evidence-hl.evidence-hl--unmatched]:bg-orange-100",
        "[&_.evidence-hl.evidence-hl--unmatched:hover]:bg-orange-200",
        "[&_.evidence-hl.evidence-hl--unmatched.evidence-hl--focused]:bg-orange-200",
        "[&_.evidence-hl.evidence-hl--unmatched.evidence-hl--focused:hover]:bg-orange-300",
        "[&_.evidence-hl.evidence-hl--unmatched.evidence-hl--active]:bg-orange-300 [&_.evidence-hl.evidence-hl--unmatched.evidence-hl--active]:shadow-[inset_0_-2px_0_theme(colors.orange.500)]",
        // Underlines: solid = coded, dashed = not coded; each nesting level sits lower so overlaps stack.
        "[&_.indication-hl]:underline [&_.indication-hl]:decoration-2 [&_.indication-hl]:underline-offset-[3px]",
        "[&_.indication-hl_.indication-hl]:underline-offset-[7px]",
        "[&_.indication-hl_.indication-hl_.indication-hl]:underline-offset-[11px]",
        "[&_.indication-hl--coded]:decoration-solid [&_.indication-hl--coded]:decoration-emerald-600",
        "[&_.indication-hl--not-coded]:decoration-dashed [&_.indication-hl--not-coded]:decoration-slate-500",
        "[&_.indication-hl--failed]:decoration-amber-500 [&_.indication-hl--failed]:before:content-['⚠_']",
        "[&_.indication-hl--active]:decoration-4 [&_.indication-hl--active]:bg-emerald-50",
        className,
      )}
    />
  );
}
